import React from 'react';
import { Box, Typography, IconButton, Collapse, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { generateTestId } from '../../../app/functions';
import HtmlContent from 'components/html/HtmlContent';

const QuestionBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  alignSelf: 'stretch',
  width: '100%',
  textAlign: 'right',
  direction: 'rtl',
  border: '1px solid #D8D8EC',
  borderRadius:'8px' ,
  [theme.breakpoints.up('md')]: {
    border: 'none',
    borderRadius: '0px',
    borderBottom: '1px solid #D8D8EC',
  },
}));

const QuestionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection:  'row-reverse',
  alignItems: 'center',
  cursor: 'pointer',
  width: '100%',
  padding: '16px',
  textAlign: 'right',
  direction: 'rtl',
  '& svg': {
    flexShrink: 0,
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    maxHeight: '50px',
    paddingBottom: '40px',
    paddingRight: '0px',
    paddingLeft: '0px',
    gap: '10px',
  },
  [theme.breakpoints.up('lg')]: {
    maxHeight: '45px',
    paddingBottom: '45px',
  },
}));

const StyledIconButton = styled(IconButton)`
  padding: 0;
  min-width: 20px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 2px;
  margin-left: auto;
  &:hover {
    background-color: transparent;
  }
`;

const QuestionText = styled(Typography)(({ theme }) => ({
  color:theme.palette.text.answer,
  fontSize: '15px',
  lineHeight: '1.5',
  flex: 1,
  textAlign: 'left',
  direction: 'ltr',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px',
    color: theme.palette.text.question,
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '18px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '20px',
  },
}));

const AnswerText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.answer,
  fontSize: '14px',
  lineHeight: '1.5',
  padding: '16px',
  textAlign: 'left',
  direction: 'ltr',
  whiteSpace: 'pre-line',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px',
  },
  [theme.breakpoints.up('md')]: {
    paddingTop: '0px',
    fontSize: '18px',

  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '20px',
  },
}));

const StyledCollapse = styled(Collapse)`
  width: 100%;
  text-align: right;
  direction: rtl;
`;

function FaqItem({ question, answer, isExpanded, onToggle, testID,}) {
  const theme = useTheme();
  
  return (
    <QuestionBox testID={generateTestId(testID, 'question-box')}>
      <QuestionHeader onClick={onToggle} testID={generateTestId(testID, 'question-header')}>
        <QuestionText
        sx={{
          fontWeight: isExpanded ? 700 : 400,
        }}
        testID={generateTestId(testID, 'question-text')}>{question}</QuestionText>
        <StyledIconButton
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-expanded={isExpanded}
          aria-label="toggle faq"
          testID={generateTestId(testID, 'toggle-button')}
        >
          {isExpanded ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" testID={generateTestId(testID, 'minus-icon')}>
              <rect width="20" height="20" rx="2" fill={theme.palette.icon.expanded}/>
              <path d="M4.16602 10.8307V9.16406H15.8327V10.8307H4.16602Z" fill="white"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" testID={generateTestId(testID, 'plus-icon')}>
              <rect width="20" height="20" rx="2" fill={theme.palette.icon.collapsed}/>
              <path d="M9.16667 17.5V10.8333H2.5V9.16667H9.16667V2.5H10.8333V9.16667H17.5V10.8333H10.8333V17.5H9.16667Z" fill="white"/>
            </svg>
          )}
        </StyledIconButton>
      </QuestionHeader>
      <StyledCollapse in={isExpanded} timeout={300} testID={generateTestId(testID, 'content')}>
        <AnswerText component="div" testID={generateTestId(testID, 'answer-text')}>
          <HtmlContent content={answer} />
        </AnswerText>
      </StyledCollapse>
    </QuestionBox>
  );
}

export default FaqItem; 